import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// Source: docs/scenario_pages_plan.md — "The Random Symptoms That Weren't Random".

export const histamineSaoirse: Scenario = {
  slug: 'histamine-saoirse',
  series: 'Food Reactions',
  title: "Three years of random symptoms. They weren't random.",
  socialHook:
    "Saoirse, 31: wine, aged cheese, leftovers — sometimes fine, sometimes not. It looked random until her results showed a histamine bucket that filled too fast and emptied too slowly.",
  meta: {
    description:
      "A fictional case study: Saoirse's flushing, headaches, and racing heart were repeatedly labeled anxiety or IBS. Her AOC1 and HNMT results show a histamine-clearing problem, not a classic allergy.",
  },

  persona: {
    name: 'Saoirse',
    age: 31,
    sex: 'Female',
    ancestry: 'Irish',
    occupation: 'Graphic designer',
    chiefConcern:
      "For three years she has had flushing, headaches, a racing heart, anxiety, and a stuffy nose after meals, but the triggers never look consistent. She wants to know whether something biological is being missed.",
    familyHistory: "Mother: similar food sensitivities — certain wines and aged cheeses trigger headaches.",
  },

  clinicalPicture: {
    symptoms: [
      'Facial and neck flushing after meals — no consistent trigger',
      'Headaches within 1–2 hours of eating (particularly wine, aged cheese, leftovers)',
      'Racing heart and a wave of anxiety — always follows the flushing, never precedes it',
      'Symptoms reliably worse in the week before her period',
    ],
    labs: [
      { name: 'Standard allergy panel (IgE)', value: 'Negative', refRange: 'Negative', flag: 'normal' },
      { name: 'DAO enzyme activity', value: '12 U/mL', refRange: '>10 U/mL (lab ref); functional threshold >20', flag: 'low' },
    ],
    medications: ['No prescription medications'],
    supplements: ['None currently'],
    lifestyle: [
      'Diet: eclectic — includes fermented foods, avocado, spinach, wine (considers herself a healthy eater)',
      'Sleep: 7–8 hours, generally good',
      'Exercise: cycling 2–3x/week',
      'Alcohol: 2–4 drinks/week, mostly wine',
      'Non-smoker',
      'Menstrual cycle: regular; episodes noticeably worse in the 5–7 days before her period',
    ],
  },

  genetics: [
    {
      gene: 'AOC1',
      variant: 'Reduced-function variants',
      rsid: 'rs10156191 + rs2052129',
      genotype: 'Compound reduced-function',
      plainMeaning:
        "Saoirse clears histamine from food more slowly than most people, so symptoms can appear once her daily load gets too high.",
      technicalDetail:
        "AOC1 encodes diamine oxidase (DAO) — the primary enzyme that breaks down dietary histamine in the gut before it enters the bloodstream. Reduced-function variants here mean less enzyme activity, so histamine from food accumulates instead of being cleared. Her DAO activity is technically above the lab reference floor but well below the functional threshold cited in the histamine intolerance literature. She has roughly 30–50% of average clearing capacity — enough to handle a normal day, not enough to handle a full histamine load.",
    },
    {
      gene: 'HNMT',
      variant: 'Reduced-function variant',
      rsid: 'rs11558538',
      genotype: 'One reduced-function copy',
      plainMeaning:
        "Her backup clearing system is also slower, so there is less room for overflow when the first pathway is busy.",
      technicalDetail:
        "HNMT (histamine N-methyltransferase) is the second histamine-clearing enzyme, working intracellularly in a different compartment from DAO. A reduced-function variant here means the backup pathway is also diminished. With both routes compromised, her total histamine clearance runs well below average. Several common factors further suppress DAO temporarily: alcohol directly blocks DAO activity; estrogen peaks around ovulation and in the days before her period both raise circulating histamine and lower DAO — which explains why her episodes are reliably worse in the second half of her cycle.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: "Three years of symptoms with no pattern",
      body: "Saoirse is 31, healthy, and active. For three years she has had episodes that seem random: flushing, a racing heart, a headache after dinner, a stuffy nose, and a wave of anxiety she cannot explain. Sometimes wine triggers it. Sometimes wine is fine. Aged cheese and leftovers do the same thing. She has been told it is anxiety, then IBS, then stress. None of it fits, because the anxiety comes after the flushing, not before.",
    },
    {
      type: 'narrative',
      heading: "Not an allergy — a full bucket",
      body: "Her allergy panel was clean because this is not a classic allergy. Histamine is found in different amounts in fermented foods, aged cheeses, red wine, cured meats, leftovers, and some common vegetables. The body normally clears it before symptoms start. When clearing cannot keep up, the bucket fills. That explains the inconsistency: one glass of wine on a low-histamine day may be fine, but the same glass after aged cheese and leftovers, right before her period, can tip the bucket over.",
    },
    {
      type: 'reveal',
      heading: "Her bucket was smaller than average",
      body: "The clue is capacity, not a single forbidden food. Saoirse's DAO activity is above the lab's low cutoff, but still below the functional threshold often discussed in histamine intolerance. Her AOC1 results explain why: she has less capacity to clear histamine in the gut before it reaches the bloodstream. The bucket was smaller than average, and she had never known it.",
    },
    {
      type: 'reveal',
      heading: "The backup system was slower too",
      body: "Once the first clearing pathway is stretched, the body needs a backup. Saoirse's HNMT result suggests that backup is also slower. That makes everyday timing matter. Alcohol can suppress DAO activity, and estrogen shifts around ovulation and before her period can raise histamine while lowering DAO. The triggers were not truly random. They were the same threshold being reached from different directions.",
    },
    {
      type: 'action',
      heading: "Five things to discuss with her clinician",
      items: [
        "Try a structured low-histamine elimination for four weeks, then reintroduce foods systematically. A dietitian familiar with histamine intolerance can help because many 'healthy' foods are high histamine.",
        "Ask whether a DAO enzyme supplement is reasonable before high-histamine meals. It may act as a bridge for restaurants or social events, not a substitute for finding the pattern.",
        "Track the bucket-fillers, especially alcohol. Wine can add histamine and also lower clearing capacity, so the question becomes how full the bucket already is.",
        "Track symptoms against her menstrual cycle for two or three cycles. The week before her period may be a predictable higher-risk window.",
        "Review medications with her family doctor. Some common medicines can temporarily lower histamine-clearing capacity, so the full list matters.",
      ],
    },
  ],

  extraBeats: [
    {
      type: 'checkin' as const,
      heading: "Saoirse's 14-day check-in",
      goalLabel: 'Daily symptoms — headaches, flushing & energy',
      caption:
        "Saoirse started a strict low-histamine diet on day 1. The improvement was gradual. Day 10 was a deliberate test with one glass of wine, and the spike made the pattern clear.",
      days: [
        {
          day: 1,
          scores: [
            { label: 'Headache', value: 7 },
            { label: 'Flushing', value: 6 },
            { label: 'Energy', value: 4 },
          ],
          note: "Started the low-histamine diet today. Still have a headache from yesterday.",
        },
        {
          day: 2,
          scores: [
            { label: 'Headache', value: 6 },
            { label: 'Flushing', value: 5 },
            { label: 'Energy', value: 4 },
          ],
          note: "Slight improvement but still there. Finding the food list harder than expected.",
        },
        {
          day: 3,
          scores: [
            { label: 'Headache', value: 5 },
            { label: 'Flushing', value: 4 },
            { label: 'Energy', value: 5 },
          ],
          note: "Bit better today. Hard to tell if the diet is working or if it would have passed anyway.",
        },
        {
          day: 4,
          scores: [
            { label: 'Headache', value: 1 },
            { label: 'Flushing', value: 2 },
            { label: 'Energy', value: 6 },
          ],
          note: "No headache today.",
          milestone: "First day without a headache",
        },
        {
          day: 5,
          scores: [
            { label: 'Headache', value: 2 },
            { label: 'Flushing', value: 2 },
            { label: 'Energy', value: 6 },
          ],
          note: "Small headache in the afternoon but nothing like before. Face feels less puffy.",
        },
        {
          day: 6,
          scores: [
            { label: 'Headache', value: 1 },
            { label: 'Flushing', value: 1 },
            { label: 'Energy', value: 7 },
          ],
          note: "Really good day. Noticed I didn't have to blow my nose at dinner.",
        },
        {
          day: 7,
          scores: [
            { label: 'Headache', value: 1 },
            { label: 'Flushing', value: 2 },
            { label: 'Energy', value: 7 },
          ],
          note: "One week in. This is the longest I have gone without a proper episode.",
        },
        {
          day: 8,
          scores: [
            { label: 'Headache', value: 1 },
            { label: 'Flushing', value: 1 },
            { label: 'Energy', value: 7 },
          ],
          note: "Feeling genuinely well. Meal prepped for the week — all low-histamine.",
        },
        {
          day: 9,
          scores: [
            { label: 'Headache', value: 2 },
            { label: 'Flushing', value: 1 },
            { label: 'Energy', value: 6 },
          ],
          note: "Slightly heavier day at work. Minor headache but recovered by evening.",
        },
        {
          day: 10,
          scores: [
            { label: 'Headache', value: 8 },
            { label: 'Flushing', value: 7 },
            { label: 'Energy', value: 3 },
          ],
          note: "Had one glass of wine at dinner as a test. Flushing within 30 minutes, headache by 10pm. Back.",
          milestone: "Reintroduction test — one glass of wine",
        },
        {
          day: 11,
          scores: [
            { label: 'Headache', value: 5 },
            { label: 'Flushing', value: 3 },
            { label: 'Energy', value: 4 },
          ],
          note: "Still recovering from yesterday. Back on the strict diet.",
        },
        {
          day: 12,
          scores: [
            { label: 'Headache', value: 2 },
            { label: 'Flushing', value: 2 },
            { label: 'Energy', value: 6 },
          ],
          note: "Coming back down. The contrast made the pattern really clear.",
        },
        {
          day: 13,
          scores: [
            { label: 'Headache', value: 1 },
            { label: 'Flushing', value: 1 },
            { label: 'Energy', value: 7 },
          ],
          note: "Back to baseline. Feeling good again.",
        },
        {
          day: 14,
          scores: [
            { label: 'Headache', value: 1 },
            { label: 'Flushing', value: 1 },
            { label: 'Energy', value: 7 },
          ],
          note: "Two weeks. I finally understand what was happening. The test on day 10 was the confirmation I needed.",
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'histamine-saoirse',
    label: 'Explore your histamine & DAO genetics in Coach',
  },
}
